import { useCallback, useEffect, useRef, useState } from 'react'
import { guildBannerUrl } from '@/lib/avatar'
import styles from './GuildBanner.module.css'

const PLAY_MS = 5000
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Баннер сервера над каналами, как у Discord. Анимированный (хэш с a_, 3-й уровень) играет 5 секунд после
 * открытия, при наведении и когда снова показался после прокрутки, а в остальное время стоит на первом кадре.
 * При «уменьшить движение» сам не играет, только при наведении. На уровне ниже 3-го — всегда первый кадр.
 */
export function GuildBanner({ guildId, hash, animate }: { guildId: string; hash: string; animate: boolean }) {
  const url = guildBannerUrl(guildId, hash)
  const animated = hash.startsWith('a_')
  const moving = animate && animated
  const box = useRef<HTMLDivElement>(null)
  const still = useRef<HTMLCanvasElement>(null)
  const timer = useRef(0)
  const [playing, setPlaying] = useState(() => moving && !reducedMotion())

  const playFor = useCallback((ms: number | null) => {
    window.clearTimeout(timer.current)
    setPlaying(true)
    if (ms !== null) timer.current = window.setTimeout(() => setPlaying(false), ms)
  }, [])
  const stop = useCallback(() => {
    window.clearTimeout(timer.current)
    setPlaying(false)
  }, [])

  // первый кадр — на холст: у анимированной картинки drawImage берёт именно его
  useEffect(() => {
    if (!animated || !url) return
    const img = new Image()
    img.onload = () => {
      const canvas = still.current
      if (!canvas) return
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      canvas.getContext('2d')?.drawImage(img, 0, 0)
    }
    img.src = url
  }, [animated, url])

  // после открытия играет 5 секунд; ушёл из виду при прокрутке и вернулся — ещё 5
  useEffect(() => {
    if (!moving) return
    timer.current = window.setTimeout(() => setPlaying(false), PLAY_MS)
    const el = box.current
    let visible = true
    const io = el
      ? new IntersectionObserver(([entry]) => {
          const now = Boolean(entry?.isIntersecting)
          if (now && !visible && !reducedMotion()) playFor(PLAY_MS)
          visible = now
        })
      : null
    if (el) io?.observe(el)
    return () => {
      io?.disconnect()
      window.clearTimeout(timer.current)
    }
  }, [moving, playFor])

  if (!url) return null
  return (
    <div
      ref={box}
      className={styles.banner}
      onMouseEnter={moving ? () => playFor(null) : undefined}
      onMouseLeave={moving ? stop : undefined}
      aria-hidden="true"
    >
      {animated ? (
        <>
          {playing ? <img src={url} alt="" draggable={false} /> : null}
          <canvas ref={still} className={playing ? styles.hidden : undefined} />
        </>
      ) : (
        <img src={url} alt="" draggable={false} />
      )}
    </div>
  )
}
